import React, { useEffect, useMemo, useState, useRef } from "react";
import { useSelector } from "react-redux";
import _ from "lodash";
import Button from "@mui/material/Button";
import s from "../../page/SurveyAnswer/style.module.scss";
import Container from "../Container";
import AccountMenu from "../AccountMenu";
import Footer from "../Footer";
import sc from "../../pres/Container/style.module.scss";
import RenderQuestion from "../RenderQuestion";
import { displayDateMessage } from "../../../utils/validateDate";
import { validateQuestions } from "../../../utils/validateQuestions";
import { setFocus } from "../../../utils/setFocus";
import { getTranslation } from "../../../locales";
import { MAX_MOBILE_SIZE, QUESTION_TYPES, RESUME } from "../../constants";
import {
  fetchCustomVariableOptionsFromAnswers,
  fetchEquationVariableOptionsFromAnswers,
  checkSurveyHasEquationVariable,
  checkSurveyHasAnswerOptionLogic,
} from "../../../services/commons";

const RenderSurveyInClassicMode = ({
  hasConditions,
  activeGroup,
  hiddenGroup = [],
  surveyId,
  currentLang,
  handleValidateQuestion,
  onAutoSave,
  resumeType,
  handleAnswerCallback,
  nextActiveQuestions = [],
}) => {
  const { previewing, hasMobileAuth } = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      previewing: urlParams.get("previewing"),
      hasMobileAuth: urlParams.get("hasMobileAuth"),
    };
  }, []);
  const {
    currentTheme,
    surveyExperienceType,
    data: survey,
  } = useSelector((state) => state.survey);
  const [answers, setAnswers] = useState({});
  const [invalidQuestions, setInvalidQuestions] = useState([]);
  const [saveAttempted, setSaveAttempted] = useState(false);
  const answerContactRef = useRef(null);
  const invalidQuestionsRef = useRef(null);
  const validationFocus = useRef(false);
  const isMobile = window.innerWidth < MAX_MOBILE_SIZE;
  const { visibleThemeHeaderNLogo } = survey[surveyId].thirdPartySettings;
  const { data: autoSavedData, dataExist: autoSaveExist } = useSelector(
    (state) => state.autoSave
  );
  const questions = useMemo(() => {
    let questions = activeGroup ? activeGroup : survey[surveyId]?.questions;
    return questions.filter(
      (q) => q.type !== QUESTION_TYPES.BLOCK && q.type !== "variable"
    );
  }, [activeGroup, survey, surveyId]);

  const anonymousAccess =
    Object.values(survey)[0]?.thirdPartySettings?.anonymous_access || false;

  useEffect(() => {
    if (validationFocus.current) {
      if (invalidQuestionsRef.current?.length > 0) {
        invalidQuestionsRef.current[0].scrollIntoView();
      }
      validationFocus.current = false;
    }
  });

  useEffect(() => {
    if (resumeType === RESUME && autoSaveExist) {
      setAnswers(autoSavedData.answers);
    }
  }, [resumeType]);

  useEffect(() => {
    const dataToSave = {
      answers,
    };
    onAutoSave(dataToSave);
    if (handleAnswerCallback) {
      handleAnswerCallback(answers);
    }
  }, [answers, activeGroup]);

  const handleValidateClassicQuestions = () => {
    const invalidQues = validateQuestions(
      surveyExperienceType,
      questions,
      answers,
      invalidQuestions,
      answerContactRef
    );
    setInvalidQuestions(invalidQues);
    setSaveAttempted(true);
    if (invalidQues.length > 0) {
      validationFocus.current = true;
      setFocus(invalidQuestions[0] + "container", true);
    } else {
      let updatedAnswer = _.cloneDeep(answers);
      if (checkSurveyHasEquationVariable(survey, surveyId)) {
        updatedAnswer = fetchEquationVariableOptionsFromAnswers(
          survey,
          surveyId,
          answers
        );
      }
      if (checkSurveyHasAnswerOptionLogic(survey, surveyId)) {
        updatedAnswer = fetchCustomVariableOptionsFromAnswers(
          survey,
          surveyId,
          answers
        );
      }
      handleValidateQuestion(updatedAnswer);
    }
  };

  return (
    <>
      <Container className={sc.main}>
        <div className={s.surveyContainer}>
          <div className={s.surveyQuestionsWrapper}>
            {/* Render all other Question Types except terminate. */}
            {questions
              .filter((q) => q.type !== "terminate")
              .map(
                (q, i) =>
                  (previewing ||
                    (q.type !== "hidden" &&
                      !q.formattingType &&
                      q.type !== "variable")) && (
                    <div
                      key={q.id}
                      ref={
                        saveAttempted && invalidQuestions.indexOf(q.id) >= 0
                          ? invalidQuestionsRef
                          : null
                      }
                      data-testid={"questionWrapper-" + i}
                      className={
                        s.questionWrapper +
                        " " +
                        (saveAttempted && invalidQuestions.indexOf(q.id) >= 0
                          ? "invalidQuestion"
                          : "")
                      }
                    >
                      <div className={s.question} id={q.id + "container"}>
                        <RenderQuestion
                          questions={survey[surveyId].questions} //to fetch the options from answers for range categorical
                          q={q}
                          answers={answers}
                          setAnswers={setAnswers}
                          surveyId={surveyId}
                          ref={answerContactRef}
                          currentLang={currentLang}
                        />
                        {q.type === "datetime" ? (
                          <p className={s.errorText} id={`${q.id}-error`}>
                            {displayDateMessage(
                              q,
                              currentLang,
                              surveyExperienceType,
                              true,
                              saveAttempted &&
                                invalidQuestions.indexOf(q.id) >= 0,
                              answers
                            )}
                          </p>
                        ) : (
                          <p
                            className={s.errorText}
                            id={`${q.id}-error`}
                            aria-label={
                              saveAttempted &&
                              invalidQuestions.indexOf(q.id) >= 0 &&
                              (q.required === true || q.type === "contact_form")
                                ? getTranslation(
                                    currentLang,
                                    "surveyTaker:requiredFieldError"
                                  )
                                : ""
                            }
                          >
                            {saveAttempted &&
                            invalidQuestions.indexOf(q.id) >= 0 &&
                            (q.required === true || q.type === "contact_form")
                              ? getTranslation(
                                  currentLang,
                                  "surveyTaker:requiredFieldError"
                                )
                              : ""}
                          </p>
                        )}
                      </div>
                    </div>
                  )
              )}
          </div>
          <div className={s.submitWrap}>
            <Button
              data-testid={hasConditions ? "conditional-next-btn" : "submit"}
              className={hasConditions ? s.submit : s.button}
              style={{
                backgroundColor: currentTheme.primaryColor,
                color: currentTheme.fontColor,
              }}
              variant="contained"
              color="primary"
              onClick={handleValidateClassicQuestions}
            >
              {hiddenGroup.length > 0 || nextActiveQuestions.length > 0
                ? getTranslation(currentLang, "surveyTaker:next")
                : getTranslation(currentLang, "surveyTaker:submit")}
            </Button>
          </div>
        </div>
      </Container>
      <div>
        {isMobile &&
          (visibleThemeHeaderNLogo ? (
            ""
          ) : (
            <div>
              <div className={s.topheader}>
                <div className={s.privacyPolicy}>
                  <AccountMenu
                    hasMobileAuth={hasMobileAuth && hasMobileAuth === "true"}
                    surveyTaker={true}
                    anonymousAccess={anonymousAccess}
                  />
                </div>
              </div>
            </div>
          ))}
        <Footer isMobile={isMobile} currentLang={currentLang} />
      </div>
    </>
  );
};

export default RenderSurveyInClassicMode;
